import { useState, useEffect, useContext } from 'react';
import styled, { css } from 'styled-components';
import {
  Loading,
  ButtonContainer,
  InputCheck,
  InputWrapper,
  Modal,
  PrimaryAltButton,
  PrimaryButton,
  SecondaryButton,
  SectionHeading,
  SectionPreTitle,
  SectionSubTitle,
  SectionTitle,
  SectionWrapper
} from '../atoms';
import { FormattedDate } from '../../common/utils/formatters';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { APIEndPoints, Request } from '../../apis';
import { UserContext } from '../../contexts';

type TRequestDifferentTermModal = {
  modalTitle?: string;
  id?: string;
  mambuAccountId: string;
  loanApplicationId: string;
  creationDate: string;
  isModalOpen: boolean;
  handleModalClose: () => void;
  refreshPage: () => void;
};

type TLoanModalDetail =
  | {
      loanTermInMonths: string | null;
      repaymentFrequency: string | null;
      courseAmount: string | null;
      firstRepaymentDate: string;
      feesSnapshotId: string;
    }
  | null
  | false;

type TAvailableTerms = Array<number> | null | false;

type TEstimateFees = {
  firstRepayment: number;
  followingRepayment: number;
  lastRepayment: number;
  totalRepayment: number;
  numFollowingRepayments: number;
  accountEstablishmentFee: number;
  accountEstablishmentFeeAmortized: boolean;
  accountServiceFee: number;
  accountNumberOfInstallments: number;
} | null;

const calculatePlanDetailsFormSchema = z.object({
  planAmount: z.number(),
  frequency: z.string(),
  planTerm: z.string()
});

type CalculatePlanDetailsFormSchema = z.infer<
  typeof calculatePlanDetailsFormSchema
>;

type TCalculateFields = {
  planAmount: number;
  frequency: string;
  planTerm: string;
};

const acceptanceFormSchema = z.object({
  acceptNewTerms: z.literal<boolean>(true, {
    errorMap: () => ({ message: 'You must tick this' })
  })
});

type AcceptanceFormSchema = z.infer<typeof acceptanceFormSchema>;

type TAcceptanceFields = {
  acceptNewTerms: boolean;
};

const FormFooter = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 45px;
`;

const SuccessIcon = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.main.secondary};
    font-size: 45px !important;
  `
);

const SuccessHeading = styled((props) => <SectionHeading {...props} />)`
  color: ${({ theme }) => theme.colors.main.secondary};
`;

const ErrorIcon = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.main.error};
    font-size: 45px !important;
  `
);

const ErrorHeading = styled((props) => <SectionHeading {...props} />)`
  color: ${({ theme }) => theme.colors.main.error};
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.main.error};
`;

const RequestDifferentTermModal = ({
  modalTitle,
  id,
  mambuAccountId,
  loanApplicationId,
  creationDate,
  isModalOpen,
  handleModalClose,
  refreshPage
}: TRequestDifferentTermModal) => {
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [calculateError, setCalculateError] = useState<string | null>(null);
  const [loanDetails, setLoanDetails] = useState<TLoanModalDetail>(null);
  const [availableTerms, setAvailableTerms] = useState<TAvailableTerms>(null);
  const [stage, setStage] = useState(1);
  const [newCourseAmount, setNewCourseAmount] = useState<number | undefined>(
    undefined
  );
  const [newTermMonths, setNewLoanTermMonths] = useState<string | undefined>(
    undefined
  );
  const [needsRecalculation, setNeedsRecalculation] = useState(false);
  const [estimatedFees, setEstimatedFees] = useState<TEstimateFees>(null);

  const [shouldRefresh, setShouldRefresh] = useState<boolean>(false);

  const { user } = useContext(UserContext);
  const merchantId = user.merchantId;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<CalculatePlanDetailsFormSchema>({
    resolver: zodResolver(calculatePlanDetailsFormSchema)
  });

  const {
    register: acceptanceRegister,
    handleSubmit: acceptanceHandleSubmit,
    reset: acceptanceReset,
    formState: { errors: acceptanceErrors }
  } = useForm<AcceptanceFormSchema>({
    resolver: zodResolver(acceptanceFormSchema)
  });

  useEffect(() => {
    if (
      isModalOpen === true &&
      (loanDetails === null || loanDetails === false)
    ) {
      const getLoans = async () => {
        const loanDetailsSchema = z.object({
          loanTermInMonths: z.string().nullable(),
          repaymentFrequency: z.string().nullable(),
          courseAmount: z.string().nullable(),
          firstRepaymentDate: z.string(),
          feesSnapshotId: z.string()
        });

        const details = await Request.safeGet({
          path: `${APIEndPoints.applicationMambuLoanDetails}/${mambuAccountId}`,
          schema: loanDetailsSchema
        });

        if (details.type === 'right') {
          setLoanDetails(details.value);
          getAvailableTerms();
        } else {
          setLoanDetails(false);
        }
      };

      const getAvailableTerms = async () => {
        const availableTermsSchema = z.array(
          z.object({
            availableTerms: z.array(z.number()),
            status: z.string(),
            productArchived: z.boolean()
          })
        );

        const availableProducts = await Request.safeGet({
          path: `${APIEndPoints.products}/merchant/${merchantId}/productType/PAS`,
          schema: availableTermsSchema
        });

        if (availableProducts.type === 'right') {
          const activeProduct = availableProducts.value.filter((product) => {
            if (
              product.status === 'ACTIVE' &&
              product.productArchived === false
            ) {
              return product;
            }
            return false;
          });

          if (activeProduct.length > 0) {
            // ONLY works with 1 active product per product type
            setAvailableTerms(activeProduct[0].availableTerms);
          } else {
            setAvailableTerms(false);
          }
        } else {
          setAvailableTerms(false);
        }
      };

      getLoans();
    }
  }, [isModalOpen]);

  // hide button when modal close
  useEffect(() => {
    if (isModalOpen === false && shouldRefresh === true) {
      refreshPage();
      setShouldRefresh(false);
    }
  }, [isModalOpen, refreshPage, shouldRefresh]);

  const onSubmit: SubmitHandler<CalculatePlanDetailsFormSchema> = async (
    data: TCalculateFields
  ) => {
    setCalculating(true);
    const loanTerm = data.planTerm;
    const loanAmount = data.planAmount;
    let startDate;
    let feeSnapshotId;
    let repaymentFrequency;

    if (loanDetails) {
      startDate = loanDetails.firstRepaymentDate;
      feeSnapshotId = loanDetails.feesSnapshotId;
      repaymentFrequency = loanDetails.repaymentFrequency;
    } else {
      setCalculateError('Missing Required Loan Details');
      setCalculating(false);
    }

    const calculatingFeesSchema = z.object({
      firstRepayment: z.number(),
      followingRepayment: z.number(),
      lastRepayment: z.number(),
      totalRepayment: z.number(),
      numFollowingRepayments: z.number(),
      accountEstablishmentFee: z.number(),
      accountEstablishmentFeeAmortized: z.boolean(),
      accountServiceFee: z.number(),
      accountNumberOfInstallments: z.number()
    });

    try {
      const response = await Request.safePost(
        `${APIEndPoints.estimateFees}/?productType=PAS&feeSnapshotId=${feeSnapshotId}&startDate=${startDate}&loanTerm=${loanTerm}&loanAmount=${loanAmount}&repaymentFrequency=${repaymentFrequency}`,
        calculatingFeesSchema
      );

      if (response.type === 'right') {
        setNeedsRecalculation(false);
        setCalculateError(null);
        setStage(2);
        setNewCourseAmount(data.planAmount);
        setNewLoanTermMonths(data.planTerm);
        setEstimatedFees(response.value);
        setCalculating(false);
      } else {
        setCalculateError('There was an issue estimating fees');
        setCalculating(false);
      }
    } catch {
      setCalculateError('There was an issue estimating fees');
      setCalculating(false);
    }
  };

  const onSubmitAcceptance: SubmitHandler<AcceptanceFormSchema> = async (
    data: TAcceptanceFields
  ) => {
    setProcessing(true);

    if (!isFinite(newCourseAmount as number) || !estimatedFees) {
      setCalculateError('Missing Required Loan Details');
      setProcessing(false);
      return;
    }

    const totalOutstanding = estimatedFees?.accountEstablishmentFeeAmortized
      ? (newCourseAmount as number) + estimatedFees?.accountEstablishmentFee // add amortized establishment fee
      : newCourseAmount; // without fees
    const numberOfInstalments = estimatedFees?.accountNumberOfInstallments;
    const frequency = loanDetails ? loanDetails.repaymentFrequency : undefined;

    if (!totalOutstanding && !numberOfInstalments && !frequency) {
      setCalculateError('Missing Required Loan Details');
      setProcessing(false);
    }

    const acceptPlanSchema = z.object({
      id: z.string(),
      status: z.number(),
      message: z.string()
    });

    try {
      const response = await Request.safePost(
        `${APIEndPoints.requestNewTerms}/${mambuAccountId}`,
        acceptPlanSchema,
        {
          totalOutstanding: totalOutstanding,
          numberOfInstallments: numberOfInstalments,
          frequency: frequency,
          courseAmount: newCourseAmount,
          // PLAT-4854 pass in loanApplicaitonId
          loanApplicationId
          // nextDueDate: string; // optional
          // minDaysToFirstRepaymentDate: number; // optional
          // expectedDisbursementDate: string; // optional
        }
      );

      if (response.type === 'right') {
        setSuccess(true);
        setProcessing(false);
        setShouldRefresh(true); // hide button when modal close
      } else {
        setError(true);
        setProcessing(false);
      }
    } catch {
      setError(true);
      setProcessing(false);
    }
  };

  const handleAmountChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> // added input and select for type issue
  ) => {
    if (parseFloat(e.currentTarget.value) !== newCourseAmount) {
      setNeedsRecalculation(true);
      acceptanceReset();
    } else {
      setNeedsRecalculation(false);
    }
  };

  const handleTermChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> // added input and select for type issue
  ) => {
    if (e.currentTarget.value !== newTermMonths) {
      setNeedsRecalculation(true);
      acceptanceReset();
    } else {
      setNeedsRecalculation(false);
    }
  };

  const closeModal = () => {
    reset();
    acceptanceReset();
    setError(false);
    setSuccess(false);
    setLoanDetails(null);
    setAvailableTerms(null);
    setStage(1);
    handleModalClose();
  };

  return (
    <Modal
      handleClose={closeModal}
      isModalVisible={isModalOpen}
      content={
        <>
          {modalTitle && <SectionPreTitle>{modalTitle}</SectionPreTitle>}
          <SectionTitle>{id}</SectionTitle>
          <SectionSubTitle>
            Created:&nbsp;
            <FormattedDate date={creationDate} />
          </SectionSubTitle>

          <div>
            {(loanDetails === null || availableTerms === null) && (
              <Loading
                style={{
                  marginTop: '3em'
                }}
              />
            )}
            {/* failed loanDetail request */}
            {(loanDetails === false || availableTerms === false) && (
              <p>Missing loan details. Request can't be completed</p>
            )}
            {loanDetails && availableTerms && !success && !error && (
              <>
                {/* missing required loan details */}
                {!loanDetails.courseAmount ||
                !loanDetails.repaymentFrequency ||
                !loanDetails.loanTermInMonths ? (
                  <p>Missing loan details. Request can't be completed</p>
                ) : (
                  <>
                    {/* has required loan details */}
                    <SectionWrapper>
                      <SectionHeading>
                        Update Payment Assist with new Plan Amount and Term
                      </SectionHeading>
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <InputWrapper
                          name="planAmount"
                          defaultValue={loanDetails.courseAmount}
                          register={register}
                          type="currency"
                          label="Plan Amount"
                          helpText="This is the cost of the course under the payment plan"
                          required
                          errorMessage={errors.planAmount?.message}
                          changeFunction={handleAmountChange}
                        />
                        <InputWrapper
                          name="frequency"
                          value={loanDetails.repaymentFrequency}
                          register={register}
                          type="text"
                          label="Frequency"
                          required
                          readOnly={true}
                          errorMessage={errors.frequency?.message}
                        />
                        <InputWrapper
                          name="planTerm"
                          defaultValue={loanDetails.loanTermInMonths}
                          register={register}
                          type="select"
                          label="Plan Term"
                          required
                          complexOptions={availableTerms.map((term) => ({
                            value: term,
                            text: `${term} ${term === 1 ? `month` : `months`}`
                          }))}
                          errorMessage={errors.planTerm?.message}
                          changeFunction={handleTermChange}
                        />
                        <FormFooter
                          horizontalSpacing="10px"
                          verticalSpacing="0px"
                          justify="center"
                        >
                          {calculateError && (
                            <ErrorText>{calculateError}</ErrorText>
                          )}
                          <PrimaryAltButton
                            type="submit"
                            isLoading={calculating}
                          >
                            Calculate
                          </PrimaryAltButton>
                        </FormFooter>
                      </form>
                    </SectionWrapper>
                    {stage > 1 && estimatedFees && (
                      <>
                        <SectionWrapper>
                          <SectionHeading>
                            New Terms <b>for:</b> ${newCourseAmount},{' '}
                            {loanDetails.repaymentFrequency}, {newTermMonths}{' '}
                            {newTermMonths === `1` ? `month` : `months`}
                          </SectionHeading>
                          <form>
                            <InputWrapper
                              value={estimatedFees.accountEstablishmentFee}
                              name="establishmentFee"
                              type="currency"
                              label="Establishment Fee"
                              disabled
                            />
                            <InputWrapper
                              value={estimatedFees.accountServiceFee}
                              name="monthlyAccountFee"
                              type="currency"
                              label="Monthly account Fee"
                              disabled
                            />
                            <InputWrapper
                              value={estimatedFees.firstRepayment}
                              name="firstPayment"
                              type="currency"
                              label="First payment will be for"
                              disabled
                            />
                            <InputWrapper
                              value={estimatedFees.followingRepayment}
                              name="otherPayments"
                              type="currency"
                              label={`Followed by ${estimatedFees.numFollowingRepayments} payments of`}
                              disabled
                            />
                            <InputWrapper
                              value={estimatedFees.lastRepayment}
                              name="lastPayment"
                              type="currency"
                              label={`Last payment will be for`}
                              disabled
                            />
                            <InputWrapper
                              value={estimatedFees.totalRepayment}
                              name="totalPayments"
                              type="currency"
                              label="Total amount you will pay under this instalment plan including all monthly account fees"
                              disabled
                            />
                          </form>
                        </SectionWrapper>
                        <SectionWrapper>
                          <SectionHeading>Accept New Terms</SectionHeading>
                          <form
                            onSubmit={acceptanceHandleSubmit(
                              onSubmitAcceptance
                            )}
                          >
                            <InputCheck
                              name="acceptNewTerms"
                              label="Student has agreed to the new terms and I confirm the student can be offered a Payment Assist agreement"
                              register={acceptanceRegister}
                              errorMessage={
                                acceptanceErrors.acceptNewTerms?.message
                              }
                              disabled={needsRecalculation}
                            />

                            <FormFooter
                              horizontalSpacing="10px"
                              verticalSpacing="0px"
                              justify="flex-end"
                            >
                              <SecondaryButton
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeModal();
                                }}
                                isLoading={processing}
                              >
                                Cancel
                              </SecondaryButton>
                              <PrimaryButton
                                type="submit"
                                isLoading={processing}
                                disabled={needsRecalculation}
                              >
                                Continue
                              </PrimaryButton>
                            </FormFooter>
                            {/* Make sure values are recalculated if edited before submitting */}
                            {needsRecalculation && (
                              <ErrorText style={{ textAlign: 'right' }}>
                                Recalculate new terms with edited values to
                                continue
                              </ErrorText>
                            )}
                          </form>
                        </SectionWrapper>
                      </>
                    )}
                  </>
                )}
              </>
            )}
            {success && (
              <SectionWrapper>
                <SuccessIcon className="material-symbols-rounded">
                  sentiment_very_satisfied
                </SuccessIcon>
                <SuccessHeading>Success</SuccessHeading>
                <p>
                  The student has been advised of the new plan and acceptance
                  instructions via email.
                </p>
                <p>Regards, Zeefi</p>
              </SectionWrapper>
            )}
            {error && (
              <>
                <ErrorIcon className="material-symbols-rounded">
                  sentiment_dissatisfied
                </ErrorIcon>
                <ErrorHeading>
                  There was an issue processing your request
                </ErrorHeading>
              </>
            )}
          </div>
        </>
      }
    />
  );
};

export { RequestDifferentTermModal };
